import { FaEdit, FaTrash } from 'react-icons/fa'
import { formatSalesValue } from '../../../utils/formatUtils';
import CustomTooltip from '../../../Layout/Tooltip/CustomTooltip';
import { formatNumber } from '../../../utils/quantityConversion';

const MenuCostTable = ({
    costItems,
    subRecipeItems,
    handleEdit,
    handleDelete,
    handleSave,
    formData,
    profitPercentage,
    handleSubRecipeDelete,
    handleSubRecipeEdit,
    manualTotalCost,
    setManualTotalCost,
    setFormData,
    draftCostItem,
    draftSubRecipeItem,
}) => {
    const displayCostItems = (() => {
        const confirmed = costItems.filter(i => !i.isDraft);
        if (!draftCostItem) return confirmed;
        if (draftCostItem._editingIndex !== undefined) {
            return confirmed.map((item, i) =>
                i === draftCostItem._editingIndex ? draftCostItem : item
            );
        }
        return [...confirmed, draftCostItem];
    })();

    const displaySubRecipeItems = (() => {
        const confirmed = subRecipeItems.filter(i => !i.isDraft);
        if (!draftSubRecipeItem) return confirmed;
        if (draftSubRecipeItem._editingIndex !== undefined) {
            return confirmed.map((item, i) =>
                i === draftSubRecipeItem._editingIndex ? draftSubRecipeItem : item
            );
        }
        return [...confirmed, draftSubRecipeItem];
    })();

    return (
        <>
            {(displayCostItems.length > 0 || displaySubRecipeItems.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {displayCostItems.length > 0 && (
                        <div>
                            <div className="bg-white rounded-sm shadow-md overflow-hidden">
                                <div className="px-3 py-2 border-b bg-gray-50">
                                    <div className="text-sm font-semibold text-gray-700">
                                        Ingredients
                                    </div>
                                </div>

                                <div className="overflow-auto max-h-[420px] tiny-scrollbar">
                                    <table className="w-full text-center text-sm">
                                        <thead className="bg-gray-100 sticky top-0 z-20">
                                            <tr>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Ingredient
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Qty
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    UOM
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Cost
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-200">
                                            {displayCostItems.map((item, index) => (
                                                <tr
                                                    key={index}
                                                    className={`hover:bg-gray-50 ${item.isDraft ? 'bg-blue-50 opacity-75' : ''}`}
                                                >
                                                    <td className="p-2 text-gray-900">
                                                        <CustomTooltip
                                                            content={
                                                                <div className="flex flex-col gap-y-1">
                                                                    <span>
                                                                        Rate: {formatSalesValue(item.rate)}
                                                                    </span>
                                                                    <span>
                                                                        Unit: {item.unit} ({item.uom})
                                                                    </span>
                                                                </div>
                                                            }
                                                        >
                                                            <span className="cursor-pointer hover:underline">
                                                                {item.costItem}
                                                            </span>
                                                        </CustomTooltip>
                                                    </td>

                                                    <td className="p-2 text-gray-700">
                                                        {formatNumber(item.quantity)}
                                                    </td>

                                                    <td className="p-2 text-gray-700">
                                                        {item.new_uom}
                                                    </td>

                                                    <td className="p-2 font-medium text-gray-900">
                                                        {formatSalesValue(item.costPrice)}
                                                    </td>

                                                    <td className="p-2">
                                                        {item.isDraft ? (
                                                            <span className="text-xs text-blue-500 italic">pending...</span>
                                                        ) : (
                                                            <div className="flex justify-center items-center gap-1.5">
                                                                <button
                                                                    onClick={() => handleEdit(index)}
                                                                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                                                >
                                                                    <FaEdit className="h-3 w-3" />
                                                                </button>

                                                                <button
                                                                    onClick={() => handleDelete(index)}
                                                                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-red-600 hover:bg-red-50 transition-colors"
                                                                >
                                                                    <FaTrash className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {displaySubRecipeItems.length > 0 && (
                        <div>
                            <div className="bg-white rounded-sm shadow-md overflow-hidden">
                                <div className="px-3 py-2 border-b bg-gray-50">
                                    <div className="text-sm font-semibold text-gray-700">
                                        Sub Recipes
                                    </div>
                                </div>

                                <div className="overflow-auto max-h-[420px] tiny-scrollbar">
                                    <table className="w-full text-center text-sm">
                                        <thead className="bg-gray-100 sticky top-0 z-20">
                                            <tr>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Sub Recipe
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Qty
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    UOM
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Cost
                                                </th>
                                                <th className="px-2 py-2.5 font-semibold text-gray-700">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-200">
                                            {displaySubRecipeItems.map((item, index) => (
                                                <tr
                                                    key={index}
                                                    className={`hover:bg-gray-50 ${item.isDraft ? 'bg-blue-50 opacity-75' : ''}`}
                                                >
                                                    <td className="p-2 text-gray-900">
                                                        <CustomTooltip
                                                            content={
                                                                <div className="flex flex-col gap-y-1">
                                                                    <span>
                                                                        Rate: {formatSalesValue(item.rate)}
                                                                    </span>
                                                                    <span>
                                                                        Unit: {item.unit} ({item.uom})
                                                                    </span>
                                                                </div>
                                                            }
                                                        >
                                                            <span className="cursor-pointer hover:underline">
                                                                {item.subRecipeName}
                                                            </span>
                                                        </CustomTooltip>
                                                    </td>

                                                    <td className="p-2 text-gray-700">
                                                        {formatNumber(item.quantity)}
                                                    </td>

                                                    <td className="p-2 text-gray-700">
                                                        {item.new_uom}
                                                    </td>

                                                    <td className="p-2 font-medium text-gray-900">
                                                        {formatSalesValue(item.costprice)}
                                                    </td>

                                                    <td className="p-2">
                                                        {item.isDraft ? (
                                                            <span className="text-xs text-blue-500 italic">pending...</span>
                                                        ) : (
                                                            <div className="flex justify-center items-center gap-1.5">
                                                                <button
                                                                    onClick={() => handleSubRecipeEdit(index)}
                                                                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                                                >
                                                                    <FaEdit className="h-3 w-3" />
                                                                </button>

                                                                <button
                                                                    onClick={() => handleSubRecipeDelete(index)}
                                                                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-red-600 hover:bg-red-50 transition-colors"
                                                                >
                                                                    <FaTrash className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default MenuCostTable